import { useViaduckEnabled } from "duck/api/hooks";
import { useDuckFullscreen } from "duck/ui/chat/hooks/useDuckFullscreen";
import { LDFlagSet } from "launchdarkly-js-client-sdk";
import { Navigate, useLocation } from "react-router";
import { LoginCallback } from "@okta/okta-react";

import { UserInfo } from "shared/api/info/api";
import { GeneralConfigModel } from "shared/api/superadmin/api";
import { useViewport } from "shared/contexts/ViewportContext";

import NotFound from "pages/NotFound";
import {
  AI_KNOWLEDGE_PAGE_CONFIG_KEY,
  ASSETS_PAGE_CONFIG_KEY,
  CLAIM_ANALYTICS_PAGE_CONFIG_KEY,
  CONNECTIONS_PAGE_CONFIG_KEY,
  DATA_TABLES_PAGE_CONFIG_KEY,
  DIRECTIVES_PAGE_CONFIG_KEY,
  DUCK2SIEMS_JOB_STATUS_PAGE_CONFIG_KEY,
  FAILURE_MODES_PAGE_CONFIG_KEY,
  FLEETS_PAGE_CONFIG_KEY,
  GROUPS_PAGE_CONFIG_KEY,
  INSPECTION_ANALYTICS_PAGE_CONFIG_KEY,
  ISSUES_PAGE_CONFIG_KEY,
  KPI_TARGETS_PAGE_CONFIG_KEY,
  PagesConfigModel,
  REPAIR_LABOR_PAGE_CONFIG_KEY,
  REPAIR_PARTS_PAGE_CONFIG_KEY,
  REPAIRS_PAGE_CONFIG_KEY,
  SCHEDULES_PAGE_CONFIG_KEY,
  SERVICE_RECORDS_PAGE_CONFIG_KEY,
  SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY,
  TIRE_SETTINGS_PAGE_CONFIG_KEY,
  VEHICLES_PAGE_CONFIG_KEY,
  VIADUCK_PAGE_CONFIG_KEY,
  WORK_ORDERS_PAGE_CONFIG_KEY,
  WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY,
} from "pages/SuperAdmin/PagesConfig/types";

import FeatureFlaggedComponent from "features/auth/Login/FeatureFlaggedComponent";
import LoginLoading from "features/auth/Login/LoginLoading";

import { lazyRoutePage } from "services/lazyRoutePage";
import { RouteKey, routes } from "services/routes";
import { mergeSearch } from "services/urlUtils";

const Users = lazyRoutePage(() => import("pages/Admin/Users"));
const Tool = lazyRoutePage(() => import("pages/AgenticCoding/Tool/Tool"));
const Tools = lazyRoutePage(() => import("pages/AgenticCoding/Tools/Tools"));
const AIKnowledge = lazyRoutePage(
  () => import("pages/AIKnowledge/AIKnowledge")
);
const Assets = lazyRoutePage(() => import("pages/Assets"));
const Asset = lazyRoutePage(() => import("pages/Assets/Asset"));
const ClaimAnalytics = lazyRoutePage(() => import("pages/ClaimAnalytics"));
const SingleClaim = lazyRoutePage(
  () => import("pages/ClaimAnalytics/SingleClaim")
);
const Dashboard = lazyRoutePage(() => import("pages/Dashboard/Dashboard"));
const Directive = lazyRoutePage(() => import("pages/Directives/Directive"));
const Directives = lazyRoutePage(() => import("pages/Directives/Directives"));
const NewDirective = lazyRoutePage(
  () => import("pages/Directives/NewDirective")
);
const Docs = lazyRoutePage(() => import("pages/Docs"));
const DuckAssistant = lazyRoutePage(
  () => import("pages/Duck/DuckAssistant/DuckAssistant")
);
const DuckSharedThread = lazyRoutePage(
  () => import("pages/Duck/DuckSharedThread")
);
const FailureMode = lazyRoutePage(
  () => import("pages/FailureModes/FailureMode/FailureMode")
);
const FailureModes = lazyRoutePage(
  () => import("pages/FailureModes/FailureModes")
);
const Fleets = lazyRoutePage(() => import("pages/Fleets"));
const FleetView = lazyRoutePage(() => import("pages/FleetView"));
const Groups = lazyRoutePage(() => import("pages/Groups"));
const Group = lazyRoutePage(() => import("pages/Groups/Group"));
const Info = lazyRoutePage(() => import("pages/Info"));
const InspectionAnalytics = lazyRoutePage(
  () => import("pages/InspectionAnalytics")
);
const SingleInspection = lazyRoutePage(
  () => import("pages/InspectionAnalytics/SingleInspection")
);
const Investigator = lazyRoutePage(
  () => import("pages/Investigator/Investigator")
);
const Issues = lazyRoutePage(() => import("pages/Issues/Issues"));
const KpiTargets = lazyRoutePage(() => import("pages/KpiTargets/KpiTargets"));
const MetabaseDashboard = lazyRoutePage(
  () => import("pages/MetabaseDashboardPage/MetabaseDashboardPage")
);
const RepairAnalytics = lazyRoutePage(() => import("pages/RepairAnalytics"));
const AnalyticsSingleRepair = lazyRoutePage(
  () => import("pages/RepairAnalytics/SingleRepair")
);
const RepairLabor = lazyRoutePage(
  () => import("pages/RepairLabor/v2/RepairLabor")
);
const RepairParts = lazyRoutePage(
  () => import("pages/RepairParts/v2/RepairParts")
);
const Repairs = lazyRoutePage(() => import("pages/Repairs/v2/Repairs"));
const SingleRepair = lazyRoutePage(
  () => import("pages/Repairs/v2/SingleRepair")
);
const Schedules = lazyRoutePage(() => import("pages/Schedules/Schedules"));
const SchemaDashboard = lazyRoutePage(
  () => import("pages/SchemaDashboard/SchemaDashboard")
);
const SchemaEventAnalytics = lazyRoutePage(
  () => import("pages/SchemaEventAnalytics/SchemaEventAnalytics")
);
const SchemaInvestigator = lazyRoutePage(
  () => import("pages/SchemaInvestigator/SchemaInvestigator")
);
const SchemaRecord = lazyRoutePage(
  () => import("pages/SchemaRecord/SchemaRecord")
);
const SchemaRecords = lazyRoutePage(
  () => import("pages/SchemaRecords/SchemaRecords")
);
const ServiceRecords = lazyRoutePage(() => import("pages/ServiceRecords"));
const SingleCustomRecord = lazyRoutePage(
  () => import("pages/ServiceRecords/SingleCustomRecord")
);
const SiemsJobStatus = lazyRoutePage(
  () => import("pages/SiemsJobStatus/SiemsJobStatus")
);
const SignalEventsAnalytics = lazyRoutePage(
  () => import("pages/SignalEventsAnalytics")
);
const SingleSignalEventOccurrence = lazyRoutePage(
  () => import("pages/SignalEventsAnalytics/SingleSignalEventOccurrence")
);
const AttributesConfig = lazyRoutePage(
  () => import("pages/SuperAdmin/AttributesConfig/AttributesConfig")
);
const ConnectionDetail = lazyRoutePage(
  () => import("pages/SuperAdmin/Connections/ConnectionDetail/ConnectionDetail")
);
const Connections = lazyRoutePage(
  () => import("pages/SuperAdmin/Connections/Connections/Connections")
);
const DataFeedDetail = lazyRoutePage(
  () => import("pages/SuperAdmin/Connections/DataFeedDetail/DataFeedDetail")
);
const ConnectorSetup = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/Connections/NewConnection/ConnectorSetup/ConnectorSetup")
);
const NewConnection = lazyRoutePage(
  () => import("pages/SuperAdmin/Connections/NewConnection/NewConnection")
);
const NewDataFeed = lazyRoutePage(
  () => import("pages/SuperAdmin/Connections/NewDataFeed/NewDataFeed")
);
const DashboardConfigPage = lazyRoutePage(
  () => import("pages/SuperAdmin/DashboardConfig/DashboardConfig")
);
const Data = lazyRoutePage(() => import("pages/SuperAdmin/Data/Data/Data"));
const DataImport = lazyRoutePage(
  () => import("pages/SuperAdmin/Data/DataImport/DataImport")
);
const DataTableDetail = lazyRoutePage(
  () => import("pages/SuperAdmin/Data/DataTableDetail/DataTableDetail")
);
const DataTableEdit = lazyRoutePage(
  () => import("pages/SuperAdmin/Data/DataTableEdit/DataTableEdit")
);
const DataUploadDetail = lazyRoutePage(
  () => import("pages/SuperAdmin/Data/DataUploadDetail/DataUploadDetail")
);
const NewData = lazyRoutePage(
  () => import("pages/SuperAdmin/Data/NewData/NewData")
);
const ReplaceDataFeed = lazyRoutePage(
  () => import("pages/SuperAdmin/Data/ReplaceDataFeed/ReplaceDataFeed")
);
const ReplaceFeedConnection = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/Data/ReplaceDataFeed/ReplaceFeedConnection/ReplaceFeedConnection")
);
const DuckChatTools = lazyRoutePage(
  () => import("pages/SuperAdmin/DuckChatTools")
);
const DuckConfig = lazyRoutePage(
  () => import("pages/SuperAdmin/DuckConfig/DuckConfig")
);
const EmailNotifications = lazyRoutePage(
  () => import("pages/SuperAdmin/EmailNotifications")
);
const EmailPipelineWorkflowDetail = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/EmailPipelineWorkflows/EmailPipelineWorkflowDetail/EmailPipelineWorkflowDetail")
);
const EmailPipelineWorkflowNew = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/EmailPipelineWorkflows/EmailPipelineWorkflowNew/EmailPipelineWorkflowNew")
);
const EmailPipelineWorkflows = lazyRoutePage(
  () => import("pages/SuperAdmin/EmailPipelineWorkflows/EmailPipelineWorkflows")
);
const BrandingConfigPage = lazyRoutePage(
  () => import("pages/SuperAdmin/GeneralConfig/BrandingConfig")
);
const GeneralConfig = lazyRoutePage(
  () => import("pages/SuperAdmin/GeneralConfig/GeneralConfig")
);
const KnowledgeRepoManagement = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/KnowledgeRepoManagement/KnowledgeRepoManagement")
);
const Localization = lazyRoutePage(
  () => import("pages/SuperAdmin/Localization/Localization")
);
const PagesConfigPage = lazyRoutePage(
  () => import("pages/SuperAdmin/PagesConfig")
);
const SchemaAttributesConfig = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaAttributesConfig/SchemaAttributesConfig")
);
const SchemaDashboardConfig = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaDashboardConfig/SchemaDashboardConfig")
);
const SchemaDefinitionDetail = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaDefinitionDetail/SchemaDefinitionDetail")
);
const SchemaDefinitionNew = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaDefinitionNew/SchemaDefinitionNew")
);
const SchemaDefinitions = lazyRoutePage(
  () => import("pages/SuperAdmin/SchemaDefinitions/SchemaDefinitions")
);
const SchemaDetailsPageConfig = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaDetailsPageConfig/SchemaDetailsPageConfig")
);
const SchemaMobileDashboardConfig = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaMobileDashboardConfig/SchemaMobileDashboardConfig")
);
const SchemaMobileDetailsPageConfig = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaMobileDetailsPageConfig/SchemaMobileDetailsPageConfig")
);
const SchemaMobileListingPageConfig = lazyRoutePage(
  () =>
    import("pages/SuperAdmin/SchemaDefinitions/SchemaMobileListingPageConfig/SchemaMobileListingPageConfig")
);
const VehicleImages = lazyRoutePage(
  () => import("pages/SuperAdmin/VehicleImages/VehicleImages")
);
const TireSettings = lazyRoutePage(
  () => import("pages/TireSettings/TireSettings")
);
const UserPreferences = lazyRoutePage(
  () => import("pages/UserPreferences/UserPreferences")
);
const Vehicles = lazyRoutePage(() => import("pages/Vehicles"));
const Version = lazyRoutePage(() => import("pages/Version"));
const VINView = lazyRoutePage(() => import("pages/VINView"));
const WorkOrderDetails = lazyRoutePage(
  () => import("pages/WorkOrders/v2/WorkOrderDetails")
);
const WorkOrdersPage = lazyRoutePage(
  () => import("pages/WorkOrders/v2/WorkOrdersPage")
);
const NewWorkOrderVerificationPage = lazyRoutePage(
  () =>
    import("pages/WorkOrderVerifications/NewWorkOrderVerification/v2/NewWorkOrderVerification")
);
const WorkOrderVerificationDetailsPage = lazyRoutePage(
  () =>
    import("pages/WorkOrderVerifications/v2/WorkOrderVerificationDetailsPage")
);
const WorkOrderVerificationsPage = lazyRoutePage(
  () => import("pages/WorkOrderVerifications/v2/WorkOrderVerificationsPage")
);

interface RouteConfigNoComponent {
  path: string;
  title?: string;
  insecure?: boolean;
  component?: JSX.Element;
  shouldDisplay?: (props: Omit<RouteDataProps, "flags">) => boolean;
  componentFunction?: (props: RouteDataProps) => JSX.Element;
  // Extra actions for this route, shown to the left of the top bar's default
  // actions (see CommonHeaderActions).
  routeActions?: JSX.Element[];
  idUrlParameter?: "vin" | "id" | "fleetName";
  noPadding?: boolean;
  noLayout?: boolean;
}

// ensures that exactly one of component or componentFunction is defined
export type RouteConfig = RouteConfigNoComponent &
  (
    | {
        component: JSX.Element;
        componentFunction?: never;
      }
    | {
        component?: never;
        componentFunction: (props: RouteDataProps) => JSX.Element;
      }
  );

export interface RouteDataProps {
  general: GeneralConfigModel;
  pages: PagesConfigModel;
  userInfo: UserInfo;
  flags: LDFlagSet;
}

type RoutesConfig = Record<RouteKey, RouteConfig>;

const RootPage = ({
  general,
  pages,
}: Pick<RouteDataProps, "general" | "pages">) => {
  const viewPort = useViewport();
  const { search } = useLocation();
  const { isFullscreen } = useDuckFullscreen();
  const isViaDuckEnabled = useViaduckEnabled();

  if (viewPort.isMobile && general?.rootPageMobile) {
    return (
      <Navigate replace to={mergeSearch(general.rootPageMobile, search)} />
    );
  }

  // The fullscreen chat covers the page, so stay on `/` with the app layout. The
  // tenant's root page may have no left menu, like `/duck-assistant`.
  if (isFullscreen && isViaDuckEnabled) {
    return null;
  }

  if (pages.landingPage && pages.landingPage.views?.length > 0) {
    return <Dashboard config={pages.landingPage} />;
  }

  return (
    <Navigate
      replace
      to={mergeSearch(general?.rootPage || routes.vehicles, search)}
    />
  );
};

export const routesConfig: RoutesConfig = {
  [RouteKey.LoginCallback]: {
    path: routes.loginCallback,
    insecure: true,
    component: <LoginCallback loadingElement={<LoginLoading />} />,
  },
  [RouteKey.Root]: {
    path: routes.root,
    componentFunction: (props: RouteDataProps) => <RootPage {...props} />,
    noPadding: true,
  },
  [RouteKey.Docs]: {
    path: routes.docs,
    component: <Docs />,
  },
  [RouteKey.FailureModes]: {
    path: routes.failureModes,
    componentFunction: () => <FailureModes />,
    shouldDisplay: ({ pages }) => !!pages[FAILURE_MODES_PAGE_CONFIG_KEY],
  },
  [RouteKey.FailureMode]: {
    path: routes.failureMode,
    componentFunction: () => <FailureMode />,
    shouldDisplay: ({ pages }) => !!pages[FAILURE_MODES_PAGE_CONFIG_KEY],
  },
  [RouteKey.NewFailureMode]: {
    path: routes.newFailureMode,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["editFailureMode"]}>
        <FailureMode />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) => !!pages[FAILURE_MODES_PAGE_CONFIG_KEY],
  },
  [RouteKey.ClaimAnalytics]: {
    path: routes.claimAnalytics,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["claimAnalytics"]}>
        <ClaimAnalytics noParentPadding />
      </FeatureFlaggedComponent>
    ),
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[CLAIM_ANALYTICS_PAGE_CONFIG_KEY],
  },
  [RouteKey.ClaimAnalyticsView]: {
    path: routes.claimAnalyticsView,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["claimAnalytics"]}>
        <ClaimAnalytics noParentPadding />
      </FeatureFlaggedComponent>
    ),
    noPadding: true,
  },
  [RouteKey.ClaimAnalyticsDetails]: {
    path: routes.claimAnalyticsDetails,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["claimAnalytics"]}>
        <SingleClaim />
      </FeatureFlaggedComponent>
    ),
  },
  [RouteKey.RepairAnalytics]: {
    path: routes.repairAnalytics,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["repairAnalytics"]}>
        <RepairAnalytics />
      </FeatureFlaggedComponent>
    ),
  },
  [RouteKey.RepairAnalyticsDetails]: {
    path: routes.repairAnalyticsDetails,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["repairAnalytics"]}>
        <AnalyticsSingleRepair />
      </FeatureFlaggedComponent>
    ),
  },
  [RouteKey.Repairs]: {
    path: routes.repairs,
    componentFunction: () => <Repairs />,
    shouldDisplay: ({ pages }) => !!pages[REPAIRS_PAGE_CONFIG_KEY],
  },
  [RouteKey.RepairDetails]: {
    path: routes.repairDetails,
    componentFunction: () => <SingleRepair />,
    shouldDisplay: ({ pages }) => !!pages[REPAIRS_PAGE_CONFIG_KEY],
  },
  [RouteKey.InspectionAnalytics]: {
    path: routes.inspectionAnalytics,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["inspectionAnalytics"]}>
        <InspectionAnalytics />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) => !!pages[INSPECTION_ANALYTICS_PAGE_CONFIG_KEY],
  },
  [RouteKey.InspectionAnalyticsDetails]: {
    path: routes.inspectionAnalyticsDetails,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["inspectionAnalytics"]}>
        <SingleInspection />
      </FeatureFlaggedComponent>
    ),
  },
  [RouteKey.CustomRecordDetails]: {
    path: routes.customRecordDetails,
    componentFunction: () => <SingleCustomRecord />,
  },
  [RouteKey.SignalEventAnalytics]: {
    path: routes.signalEventAnalytics,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["signalEventsAnalytics"]}>
        <SignalEventsAnalytics noParentPadding />
      </FeatureFlaggedComponent>
    ),
    noPadding: true,
    shouldDisplay: ({ pages }) =>
      !!pages[SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY],
  },
  [RouteKey.SignalEventAnalyticsView]: {
    path: routes.signalEventAnalyticsView,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["signalEventsAnalytics"]}>
        <SignalEventsAnalytics noParentPadding />
      </FeatureFlaggedComponent>
    ),
    noPadding: true,
  },
  [RouteKey.SignalEventsAnalyticsDetails]: {
    path: routes.signalEventsAnalyticsDetails,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["signalEventsAnalytics"]}>
        <SingleSignalEventOccurrence />
      </FeatureFlaggedComponent>
    ),
  },
  [RouteKey.Dashboard]: {
    path: routes.dashboard,
    componentFunction: () => <MetabaseDashboard />,
    shouldDisplay: ({ general }) =>
      !!(general?.metabaseDashboards && general.metabaseDashboards?.length > 0),
  },
  [RouteKey.SchemaDashboard]: {
    path: routes.schemaDashboard,
    componentFunction: () => <SchemaDashboard />,
    noPadding: true,
  },
  [RouteKey.Vehicles]: {
    path: routes.vehicles,
    componentFunction: () => <Vehicles pageKey="vehicles" />,
    shouldDisplay: ({ pages }) => !!pages[VEHICLES_PAGE_CONFIG_KEY],
  },
  [RouteKey.Assets]: {
    path: routes.assets,
    componentFunction: () => <Assets />,
    shouldDisplay: ({ pages }) => !!pages[ASSETS_PAGE_CONFIG_KEY],
  },
  [RouteKey.Asset]: {
    path: routes.asset,
    componentFunction: () => <Asset />,
    shouldDisplay: ({ pages }) => !!pages[ASSETS_PAGE_CONFIG_KEY],
  },
  [RouteKey.VinView]: {
    path: routes.vinView,
    idUrlParameter: "vin",
    componentFunction: () => <VINView />,
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[VEHICLES_PAGE_CONFIG_KEY],
  },
  [RouteKey.Fleets]: {
    path: routes.fleets,
    componentFunction: () => <Fleets />,
    shouldDisplay: ({ pages }) => !!pages[FLEETS_PAGE_CONFIG_KEY],
  },
  [RouteKey.FleetView]: {
    path: routes.fleetView,
    idUrlParameter: "fleetName",
    componentFunction: () => <FleetView />,
    shouldDisplay: ({ pages }) => !!pages[FLEETS_PAGE_CONFIG_KEY],
  },
  [RouteKey.ServiceRecords]: {
    path: routes.serviceRecords,
    componentFunction: () => <ServiceRecords />,
    shouldDisplay: ({ pages }) => !!pages[SERVICE_RECORDS_PAGE_CONFIG_KEY],
  },
  [RouteKey.Schedules]: {
    path: routes.schedules,
    componentFunction: () => <Schedules />,
    shouldDisplay: ({ pages }) => !!pages[SCHEDULES_PAGE_CONFIG_KEY],
  },
  [RouteKey.Version]: {
    path: routes.version,
    component: <Version />,
  },
  [RouteKey.Groups]: {
    path: routes.groups,
    componentFunction: () => <Groups />,
    shouldDisplay: ({ pages }) => !!pages[GROUPS_PAGE_CONFIG_KEY],
  },
  [RouteKey.Group]: {
    path: routes.group,
    componentFunction: () => <Group />,
    shouldDisplay: ({ pages, userInfo }) =>
      userInfo.isAdmin ||
      userInfo.isSuperAdmin ||
      !!pages[GROUPS_PAGE_CONFIG_KEY],
  },
  [RouteKey.AttributesConfig]: {
    path: routes.attributesConfig,
    componentFunction: () => <AttributesConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.WorkOrders]: {
    path: routes.workOrders,
    componentFunction: () => <WorkOrdersPage />,
    shouldDisplay: ({ pages }) => !!pages[WORK_ORDERS_PAGE_CONFIG_KEY],
  },
  [RouteKey.WorkOrderDetails]: {
    path: routes.workOrderDetails,
    componentFunction: () => <WorkOrderDetails />,
    shouldDisplay: ({ pages }) => !!pages[WORK_ORDERS_PAGE_CONFIG_KEY],
  },
  [RouteKey.WorkOrderVerifications]: {
    path: routes.workOrdersVerification,
    componentFunction: () => <WorkOrderVerificationsPage />,
    shouldDisplay: ({ pages }) =>
      !!pages[WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY],
  },
  [RouteKey.WorkOrderVerificationDetails]: {
    path: routes.workOrderVerificationDetails,
    componentFunction: () => <WorkOrderVerificationDetailsPage />,
    shouldDisplay: ({ pages }) =>
      !!pages[WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY],
  },
  [RouteKey.WorkOrdersVerificationNew]: {
    path: routes.workOrdersVerificationNew,
    componentFunction: () => <NewWorkOrderVerificationPage />,
    shouldDisplay: ({ pages }) =>
      !!pages[WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY],
  },
  [RouteKey.RepairParts]: {
    path: routes.repairParts,
    componentFunction: () => <RepairParts />,
    shouldDisplay: ({ pages }) => !!pages[REPAIR_PARTS_PAGE_CONFIG_KEY],
  },
  [RouteKey.RepairLabor]: {
    path: routes.repairLabor,
    componentFunction: () => <RepairLabor />,
    shouldDisplay: ({ pages }) => !!pages[REPAIR_LABOR_PAGE_CONFIG_KEY],
  },

  [RouteKey.PagesConfig]: {
    path: routes.pagesConfig,
    componentFunction: () => <PagesConfigPage />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.DashboardConfig]: {
    path: routes.dashboardConfig,
    componentFunction: () => <DashboardConfigPage />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.GeneralConfig]: {
    path: routes.generalConfig,
    componentFunction: () => <GeneralConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.BrandingConfig]: {
    path: routes.brandingConfig,
    componentFunction: () => <BrandingConfigPage />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.AdminGroups]: {
    path: routes.adminGroups,
    componentFunction: () => <Groups adminGroupsOnly />,
    shouldDisplay: ({ userInfo }) => userInfo.isAdmin || userInfo.isSuperAdmin,
  },
  [RouteKey.Users]: {
    path: routes.users,
    componentFunction: () => <Users />,
    shouldDisplay: ({ userInfo }) => userInfo.isAdmin || userInfo.isSuperAdmin,
  },
  [RouteKey.SuperAdminGroups]: {
    path: routes.superAdminGroups,
    componentFunction: () => <Groups superAdminGroupsOnly />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.Info]: {
    path: routes.info,
    componentFunction: ({ userInfo }) =>
      userInfo.isSuperAdmin ? (
        <Info />
      ) : (
        <FeatureFlaggedComponent flags={["settingsPage"]}>
          <Info />
        </FeatureFlaggedComponent>
      ),
  },
  [RouteKey.DuckSharedThread]: {
    path: routes.duckSharedThread,
    component: <DuckSharedThread />,
    noPadding: true,
  },
  [RouteKey.DuckAssistant]: {
    path: routes.duckAssistant,
    component: <DuckAssistant />,
    shouldDisplay: ({ pages }) => !!pages[VIADUCK_PAGE_CONFIG_KEY],
    noPadding: true,
    noLayout: true,
  },
  [RouteKey.NotFound]: {
    path: routes.notFound,
    component: <NotFound />,
  },
  [RouteKey.VehicleImages]: {
    path: routes.vehicleImages,
    componentFunction: () => <VehicleImages />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.Localization]: {
    path: routes.localization,
    componentFunction: () => <Localization />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SmartGroup]: {
    path: routes.smartGroup,
    componentFunction: () => <Investigator />,
    noPadding: true,
  },
  [RouteKey.Investigator]: {
    path: routes.investigator,
    componentFunction: () => <Investigator />,
    noPadding: true,
  },
  [RouteKey.IssueInvestigator]: {
    path: routes.issueInvestigator,
    componentFunction: () => <Investigator />,
    noPadding: true,
  },
  [RouteKey.DuckConfig]: {
    path: routes.duckConfig,
    componentFunction: () => <DuckConfig />,
  },
  [RouteKey.Directives]: {
    path: routes.directives,
    componentFunction: () => <Directives />,
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[DIRECTIVES_PAGE_CONFIG_KEY],
  },
  [RouteKey.DirectiveNew]: {
    path: routes.directiveNew,
    componentFunction: () => <NewDirective />,
    // The page pads itself, so its header can stick to the top of the scroll area.
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[DIRECTIVES_PAGE_CONFIG_KEY],
  },
  [RouteKey.Directive]: {
    path: routes.directive,
    componentFunction: () => <Directive />,
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[DIRECTIVES_PAGE_CONFIG_KEY],
  },
  [RouteKey.DuckChatTools]: {
    path: routes.duckChatTools,
    componentFunction: () => <DuckChatTools />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.Data]: {
    path: routes.data,
    componentFunction: () => <Data />,
    shouldDisplay: ({ pages }) => !!pages[DATA_TABLES_PAGE_CONFIG_KEY],
  },
  [RouteKey.NewData]: {
    path: routes.newData,
    componentFunction: () => <NewData />,
    // Pads itself, since the file origin it hands over to needs its header to stick to the top of the scroll area.
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[DATA_TABLES_PAGE_CONFIG_KEY],
  },
  [RouteKey.DataUpload]: {
    path: routes.dataUpload,
    componentFunction: () => <DataUploadDetail />,
    // The page pads itself, so its header can stick to the top of the scroll area.
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[DATA_TABLES_PAGE_CONFIG_KEY],
  },
  [RouteKey.DataTable]: {
    path: routes.dataTable,
    componentFunction: () => <DataTableDetail />,
    shouldDisplay: ({ pages }) => !!pages[DATA_TABLES_PAGE_CONFIG_KEY],
  },
  [RouteKey.DataTableEdit]: {
    path: routes.dataTableEdit,
    componentFunction: () => <DataTableEdit />,
    shouldDisplay: ({ pages }) => !!pages[DATA_TABLES_PAGE_CONFIG_KEY],
  },
  [RouteKey.DataImport]: {
    path: routes.dataImport,
    componentFunction: () => <DataImport />,
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[DATA_TABLES_PAGE_CONFIG_KEY],
  },
  // Connections are gated twice: on the tenant's page config through shouldDisplay, and on the connections flag
  // around the component, since shouldDisplay cannot see flags. A feed is a Connections thing written into a data
  // table, so replacing one needs both pages.
  [RouteKey.ReplaceDataFeed]: {
    path: routes.replaceDataFeed,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <ReplaceDataFeed />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) =>
      !!pages[DATA_TABLES_PAGE_CONFIG_KEY] &&
      !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.ReplaceDataFeedConnection]: {
    path: routes.replaceDataFeedConnection,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <ReplaceFeedConnection />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) =>
      !!pages[DATA_TABLES_PAGE_CONFIG_KEY] &&
      !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.Connections]: {
    path: routes.connections,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <Connections />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) => !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.NewConnection]: {
    path: routes.newConnection,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <NewConnection />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) => !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.NewConnectorConnection]: {
    path: routes.newConnectorConnection,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <ConnectorSetup />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) => !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.Connection]: {
    path: routes.connection,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <ConnectionDetail />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) => !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.NewDataFeed]: {
    path: routes.newDataFeed,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <NewDataFeed />
      </FeatureFlaggedComponent>
    ),
    // The page pads itself, so its header can stick to the top of the scroll area.
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.DataFeed]: {
    path: routes.dataFeed,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["connections"]}>
        <DataFeedDetail />
      </FeatureFlaggedComponent>
    ),
    // The page pads itself, so its header can stick to the top of the scroll area.
    noPadding: true,
    shouldDisplay: ({ pages }) => !!pages[CONNECTIONS_PAGE_CONFIG_KEY],
  },
  [RouteKey.SchemaDefinitions]: {
    path: routes.schemaDefinitions,
    componentFunction: () => <SchemaDefinitions />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SchemaDefinition]: {
    path: routes.schemaDefinition,
    componentFunction: () => <SchemaDefinitionDetail />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SchemaDashboardConfig]: {
    path: routes.schemaDashboardConfig,
    componentFunction: () => <SchemaDashboardConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SchemaDetailsPageConfig]: {
    path: routes.schemaDetailsPageConfig,
    componentFunction: () => <SchemaDetailsPageConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SchemaMobileDashboardConfig]: {
    path: routes.schemaMobileDashboardConfig,
    componentFunction: () => <SchemaMobileDashboardConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SchemaMobileDetailsPageConfig]: {
    path: routes.schemaMobileDetailsPageConfig,
    componentFunction: () => <SchemaMobileDetailsPageConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SchemaMobileListingPageConfig]: {
    path: routes.schemaMobileListingPageConfig,
    componentFunction: () => <SchemaMobileListingPageConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.SchemaAttributesConfig]: {
    path: routes.schemaAttributesConfig,
    componentFunction: () => <SchemaAttributesConfig />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.NewSchemaDefinition]: {
    path: routes.newSchemaDefinition,
    componentFunction: () => <SchemaDefinitionNew />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.EmailNotifications]: {
    path: routes.emailNotifications,
    componentFunction: () => <EmailNotifications />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.EmailPipelineWorkflows]: {
    path: routes.emailPipelineWorkflows,
    componentFunction: () => <EmailPipelineWorkflows />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.NewEmailPipelineWorkflow]: {
    path: routes.newEmailPipelineWorkflow,
    componentFunction: () => <EmailPipelineWorkflowNew />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.EmailPipelineWorkflow]: {
    path: routes.emailPipelineWorkflow,
    componentFunction: () => <EmailPipelineWorkflowDetail />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.Issues]: {
    path: routes.issues,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["detectedIssues"]}>
        <Issues />
      </FeatureFlaggedComponent>
    ),
    shouldDisplay: ({ pages }) => !!pages[ISSUES_PAGE_CONFIG_KEY],
  },
  [RouteKey.SchemaEventAnalytics]: {
    path: routes.schemaEventAnalytics,
    componentFunction: () => <SchemaEventAnalytics />,
    noPadding: true,
  },
  [RouteKey.SchemaInvestigator]: {
    path: routes.schemaInvestigator,
    componentFunction: () => <SchemaInvestigator />,
    noPadding: true,
  },
  [RouteKey.SchemaInvestigatorById]: {
    path: routes.schemaInvestigatorById,
    componentFunction: () => <SchemaInvestigator />,
    noPadding: true,
  },
  [RouteKey.SchemaRecords]: {
    path: routes.schemaRecords,
    componentFunction: () => <SchemaRecords />,
    noPadding: true,
  },
  [RouteKey.SchemaRecord]: {
    path: routes.schemaRecord,
    componentFunction: () => <SchemaRecord />,
  },
  [RouteKey.AIKnowledge]: {
    path: routes.aiKnowledge,
    componentFunction: () => <AIKnowledge />,
    shouldDisplay: ({ pages }) => !!pages[AI_KNOWLEDGE_PAGE_CONFIG_KEY],
  },
  [RouteKey.KnowledgeRepoManagement]: {
    path: routes.knowledgeRepoManagement,
    componentFunction: () => <KnowledgeRepoManagement />,
    shouldDisplay: ({ userInfo }) => userInfo.isSuperAdmin,
  },
  [RouteKey.TireSettings]: {
    path: routes.tireSettings,
    componentFunction: () => <TireSettings />,
    shouldDisplay: ({ pages }) => !!pages[TIRE_SETTINGS_PAGE_CONFIG_KEY],
  },
  [RouteKey.KpiTargets]: {
    path: routes.kpiTargets,
    componentFunction: () => <KpiTargets />,
    shouldDisplay: ({ pages }) => !!pages[KPI_TARGETS_PAGE_CONFIG_KEY],
  },
  [RouteKey.Duck2SiemsJobStatus]: {
    path: routes.duck2SiemsJobStatus,
    componentFunction: () => <SiemsJobStatus />,
    shouldDisplay: ({ pages }) =>
      !!pages[DUCK2SIEMS_JOB_STATUS_PAGE_CONFIG_KEY],
  },
  [RouteKey.UserPreferences]: {
    path: routes.userPreferences,
    componentFunction: () => <UserPreferences />,
  },
  [RouteKey.AgenticTools]: {
    path: routes.agenticTools,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["agenticCoding"]}>
        <Tools />
      </FeatureFlaggedComponent>
    ),
  },
  [RouteKey.AgenticTool]: {
    path: routes.agenticTool,
    componentFunction: () => (
      <FeatureFlaggedComponent flags={["agenticCoding"]}>
        <Tool />
      </FeatureFlaggedComponent>
    ),
  },
};
